/**
 * Image utility functions for smart cropping
 * Uses smartcrop.js to find optimal crop positions focusing on faces
 * @module utils/imageUtils
 */

import smartcrop from 'smartcrop'

/**
 * Analyzes an image and determines the best crop position
 * Prioritizes faces and important features using smartcrop's built-in algorithms
 * @param {string} imageUrl - URL of the image to analyze
 * @param {number} width - Desired crop width
 * @param {number} height - Desired crop height
 * @returns {Promise<Object>} Crop data with topCrop containing {x, y, width, height}
 * @example
 * const crop = await getSmartCrop(imageUrl, 800, 400)
 * console.log(crop.topCrop) // {x: 100, y: 50, width: 800, height: 400}
 */
export const getSmartCrop = async (imageUrl, width, height) => {
  try {
    // Create an image element
    const img = new Image()
    img.crossOrigin = 'anonymous'

    // Load the image
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = imageUrl
    })

    // Configure smartcrop with optimal settings for face detection
    const options = {
      width,
      height,
      minScale: 1.0,
      ruleOfThirds: true,
      // Smartcrop has built-in face detection via skin tone analysis
      // These weights help it prioritize faces
      skinColor: [0.78, 0.57, 0.44],
      skinBias: 0.01,
      skinBrightnessMin: 0.2,
      skinBrightnessMax: 1.0,
      skinThreshold: 0.8,
      skinWeight: 1.8,
      saturationBrightnessMin: 0.05,
      saturationBrightnessMax: 0.9,
      saturationThreshold: 0.4,
      saturationBias: 0.2,
      saturationWeight: 0.3,
      // Step size - lower is more accurate but slower
      scoreDownSample: 8,
      step: 8,
    }

    // Analyze the image with smartcrop
    const result = await smartcrop.crop(img, options)

    return result
  } catch (error) {
    // Silently fail for CORS errors and other issues
    // Images will fall back to center-center positioning
    // Return a centered crop as fallback
    return {
      topCrop: {
        x: 0,
        y: 0,
        width: width,
        height: height,
        score: 0
      }
    }
  }
}

/**
 * Calculates CSS object-position based on smart crop result
 * Uses smartcrop's actual recommendation for precise positioning
 * @param {Object} cropData - Result from getSmartCrop
 * @param {number} imageWidth - Original image width
 * @param {number} imageHeight - Original image height
 * @returns {string} CSS object-position value (percentage-based)
 * @example
 * const position = getObjectPosition(cropData, 1200, 800)
 * // Returns something like "50.0% 30.5%" based on smartcrop analysis
 */
export const getObjectPosition = (cropData, imageWidth, imageHeight) => {
  if (!cropData || !cropData.topCrop) {
    return 'center center'
  }

  const crop = cropData.topCrop

  // Calculate the center of the crop area
  const cropCenterX = crop.x + (crop.width / 2)
  const cropCenterY = crop.y + (crop.height / 2)

  // Convert to percentages
  const xPercent = (cropCenterX / imageWidth) * 100
  const yPercent = (cropCenterY / imageHeight) * 100

  return `${xPercent.toFixed(1)}% ${yPercent.toFixed(1)}%`
}

/**
 * Cache for smart crop results to avoid re-analyzing the same images
 */
const cropCache = new Map()

/**
 * Gets smart crop with caching
 * @param {string} imageUrl - URL of the image
 * @param {number} width - Desired width
 * @param {number} height - Desired height
 * @returns {Promise<Object>} Cached or fresh crop data
 */
export const getCachedSmartCrop = async (imageUrl, width, height) => {
  // Version the cache key to force fresh analysis when algorithm changes
  const cacheVersion = 'v5'
  const cacheKey = `${cacheVersion}-${imageUrl}-${width}-${height}`

  if (cropCache.has(cacheKey)) {
    return cropCache.get(cacheKey)
  }

  const cropData = await getSmartCrop(imageUrl, width, height)
  cropCache.set(cacheKey, cropData)

  return cropData
}

/**
 * Clears the crop cache
 * Useful for memory management if the cache gets too large
 */
export const clearCropCache = () => {
  cropCache.clear()
  console.log('Crop cache cleared')
}
